import { Form, message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Id, Row } from "@silevis/reactgrid";

import ReactGridCustom from "@lhd/components/ReactGridCustom";
import { getColumns } from "@lhd/components/CloneApp/pages/SheetApp/untils";

import { IProps } from "./types";
import { CUSTOM_CELL_TEMPLATES } from "./constants";
import { useTableRows } from "./hooks/useTableRows";
import { useTableHandlers } from "./hooks/useTableHandlers";
import { useClampReactGridContextMenu } from "./hooks/useClampContextMenu";
import { StyledBtn, Styles } from "./style";
import { MIN_ROW_HEIGHT } from "./utils";
import { RowHeightContext } from "./RowHeightContext";
import { TableToggleCellTemplate } from "./cells/TableToggleCellTemplate";
import { TYPE_CELLS } from "@lhd/components/CloneApp/pages/SheetApp/contants";
import {
  buildCompanyPoliciesChanges,
  getLegalColumnIds,
} from "./companyPolicies";
import Title from "../../components/Title";
import ButtonImport from "../../LayoutApp/ButtonImport";
import Button from "@lhd/components/Button";
import { APP_TYPES, CURRENCY_UNITS } from "../../constants";
import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";
import ButtonExport from "../../LayoutApp/ButtonExport";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { BUTTON_HEIGHT, FONT_SIZE } from "@lhd/constants";

/* ======================= */
// Match the width of the amount inputs above the table (24~26 * COLUMN_WIDTH)
const BUSINESS_TRIP_CURRENCY_COLUMN_WIDTH = 150;

const INDEX_COL = {
  columnId: "index",
  width: 60,
  label: "",
  type: "text",
  resizable: false,
};

const TableSheet = ({
  item,
  selectionDataList,
  isDisabled,
  permission,
  tableData,
  appType,
  handleImportTableRows,
  onPrintPDF,
  isPrinting,
  isCreate,
  onChange,
  appParts,
  isQuickViewMode,
}: IProps) => {
  const form = Form.useFormInstance();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCtrlHToReplacesValue, setIsCtrlHToReplacesValue] = useState(false);
  const tableId = item.id?.toString() || "";
  const tablePath = ["tables", tableId];
  const tablePathValue = ["tables", tableId];

  /* =======================
     Columns
  ======================= */

  const dataColumns = useMemo(() => {
    const sorted = (item.children || []).sort(
      (a, b) => a.item_config_data.x - b.item_config_data.x,
    );

    const dataColumnsRaw = getColumns(sorted, true);

    if (appType !== APP_TYPES.BUSINESS_TRIP_REPORTS) return dataColumnsRaw;

    // Business trip reports only: getColumns forces a fixed narrow width on every
    // number column, so the amount wraps once the currency symbol is prepended
    return dataColumnsRaw.map((col) => {
      const appPart = col.appPart;
      const isCurrencyColumn =
        appPart?.app_part_type === APP_PART_TYPES.NUMBER &&
        CURRENCY_UNITS.includes((appPart.unit_of_measure || "").trim());
      if (!isCurrencyColumn) return col;

      return {
        ...col,
        width: Math.max(col.width, BUSINESS_TRIP_CURRENCY_COLUMN_WIDTH),
      };
    });
  }, [item.children, appType]);

  const columns = useMemo(
    () => [INDEX_COL, ...dataColumns],
    [dataColumns],
  ) as any;

  const manualRowHeights = useRef<Map<string, number>>(new Map());
  const getManualHeight = useCallback(
    (clientId?: string) =>
      clientId ? manualRowHeights.current.get(clientId) : undefined,
    [],
  );

  /* =======================
     Rows
  ======================= */

  const { rows, setRows, handleAddRow, handleDeleteRow, handleDeleteRows } =
    useTableRows({
      dataColumns,
      columns,
      containerRef,
      tableData,
      selectionDataList,
      form,
      tablePathValue,
      tablePath,
      permission,
      isDisabled,
      onChange,
      getManualHeight,
    });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (event: FocusEvent) => {
      if (container.contains(event.target as Node)) {
        setIsCtrlHToReplacesValue(true);
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (!container.contains(event.target as Node)) {
        setIsCtrlHToReplacesValue(false);
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isCtrlHToReplacesValue]);

  /* =======================
     Row height
  ======================= */

  // Batch height updates to avoid multiple consecutive setRows calls
  const pendingHeights = useRef<Map<string, number>>(new Map());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateRowHeight = useCallback(
    (clientId: string, rowIndex: number, explicitHeight?: number) => {
      const container = containerRef.current;
      if (!container) return;
      // Query all cells in this row by data-cell-rowidx
      const cells = container.querySelectorAll<HTMLElement>(
        `.rg-cell[data-cell-rowidx="${rowIndex}"]`,
      );
      let maxH = MIN_ROW_HEIGHT;
      cells.forEach((cell) => {
        const child = cell.firstElementChild as HTMLElement | null;
        if (child) maxH = Math.max(maxH, child.offsetHeight);
      });

      if (explicitHeight !== undefined) {
        manualRowHeights.current.set(clientId, explicitHeight);
      }
      const manualHeight = manualRowHeights.current.get(clientId);
      if (manualHeight !== undefined) {
        maxH = Math.max(maxH, manualHeight);
      }

      pendingHeights.current.set(clientId, maxH);

      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(() => {
        const updates = new Map(pendingHeights.current);
        pendingHeights.current.clear();

        setRows((prev) => {
          let changed = false;
          const next = prev.map((row) => {
            const h = updates.get(String(row.rowId));
            if (h !== undefined && Math.abs(h - (row.height ?? 0)) > 1) {
              changed = true;
              return {
                ...row,
                height: h,
                cells: row.cells.map((c) => ({ ...c, rowHeight: h })),
              };
            }
            return row;
          });
          return changed ? next : prev;
        });
      }, 50);
    },
    [setRows],
  );

  /* =======================
     Handlers
  ======================= */
  useClampReactGridContextMenu(containerRef);

  const { handleCellsChanged, handleContextMenu, handleRowsReordered } =
    useTableHandlers({
      rows,
      setRows,
      columns,
      containerRef,
      form,
      tablePathValue,
      selectionDataList,
      isDisabled,
      permission,
      handleAddRow,
      handleDeleteRow,
      handleDeleteRows,
      onChange,
      getManualHeight,
    });

  const customCellTemplates = useMemo(
    () => ({
      ...CUSTOM_CELL_TEMPLATES,
      [TYPE_CELLS.TABLE_TOGGLE_CELL]: new TableToggleCellTemplate(
        () => setIsCollapsed((prev: boolean) => !prev),
        isCollapsed,
      ),
    }),
    [isCollapsed],
  );

  const legalColumnIds = useMemo(() => getLegalColumnIds(columns), [columns]); // for second table in company_policies;
  const isSecondTablePolice =
    appType === APP_TYPES.COMPANY_POLICIES &&
    Object.keys(legalColumnIds).some(
      (key) => legalColumnIds[key as keyof typeof legalColumnIds],
    );

  const handleRewriteNumber = useCallback(() => {
    const changes = buildCompanyPoliciesChanges(rows, columns, legalColumnIds);
    if (changes.length) {
      handleCellsChanged(changes as any);
      onChange && onChange();
      message.success("番号を振り直しました。");
      onChange && onChange();
    }
  }, [rows, columns, legalColumnIds, handleCellsChanged]);

  const rowsForRender = useMemo(
    () => (isCollapsed ? rows.slice(0, 1) : rows),
    [isCollapsed, rows],
  );

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;

  /* =======================
     Render
  ======================= */

  return (
    <div>
      <StyledBtn text_scale={displaySize}>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            position: isQuickViewMode ? "static" : "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#f0f2f5",
          }}
        >
          <Title item={item} />
          {isSecondTablePolice && (
            <>
              <ButtonImport
                isDisabled={isDisabled || !permission?.edit}
                handleImportData={(rows: string[][]) =>
                  handleImportTableRows?.(item, rows)
                }
                appParts={item.children}
              />
              <ButtonExport
                tableItem={item}
                tableData={tableData}
                appParts={appParts}
              />
              <Button
                label="番号の自動振り直し"
                height={20}
                onClick={handleRewriteNumber}
                disabled={isDisabled || !permission?.edit}
              />
              {!isCreate && (
                <Button
                  label={"印刷・PDF"}
                  onClick={onPrintPDF}
                  loading={isPrinting}
                  height={20}
                />
              )}
            </>
          )}
        </div>
      </StyledBtn>

      <RowHeightContext.Provider value={{ updateRowHeight, getManualHeight }}>
        <Styles
          className={`${isDisabled ? "mode_view" : ""} ${
            isSecondTablePolice ? "second-table-police-mode" : ""
          }`}
          ref={containerRef}
          style={{ width: "100%" }}
        >
          <Form.Item name={tablePathValue} hidden noStyle />
          <ReactGridCustom
            heightTable={"auto"}
            maxHeight={`auto`}
            className="table-main-sheet"
            rows={rowsForRender}
            columns={columns}
            onCellsChanged={(changes) => {
              if (!isDisabled) {
                handleCellsChanged(changes as any);
                onChange && onChange();
              }
            }}
            onContextMenu={!isDisabled ? (handleContextMenu as any) : undefined}
            isSpecialRowHeader
            enableRangeSelection
            enableRowSelection
            stickyTopRows={isQuickViewMode ? 0 : 1}
            onRowsReordered={handleRowsReordered}
            canReorderRows={(targetRowId: Id, rowIds: Id[], dropPosition) => {
              return !isDisabled && targetRowId !== "header";
            }}
            customCellTemplates={customCellTemplates}
            isCtrlHToReplacesValue={isCtrlHToReplacesValue}
            labels={{
              copyLabel: "",
              cutLabel: "",
              pasteLabel: "",
            }}
            nameTable={item.name}
          />
        </Styles>
      </RowHeightContext.Provider>
    </div>
  );
};

export default TableSheet;
